import { useEffect, useRef, useState } from "react";
import { AutoComplete, Collapse, Input, Popover, Slider, Spin } from "antd";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import {
	getCarListing,
	getDefaultFilterValues,
	getSalvageCarListing,
} from "../../utils/api";
import { useDebounce } from "use-debounce";
import carModels from "../findCar/carModels.json";
import SalvageCarInfo from "./SalvageCarInfo";
import MarketCarInfo from "./MarketCarInfo";

const FilteredCar = ({ heading, pageType }) => {
	const isFirstRender = useRef(true);
	const location = useLocation();
	const [cars, setCars] = useState([]);
	const [query] = useSearchParams();
	const isUpdate = query.get("update");
	const [loading, setLoading] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [noOfPages, setNoOfPages] = useState(0);
	const [compare, setCompare] = useState([]);
	const [formValues, setFormValues] = useState([]);
	const [filters, setFilters] = useState({
		make: "",
		model: "",
		city: "",
		trim: "",
		radius: 50,
		price_max: "",
		price_min: "0",
		mileage: "",
		year_max: `${new Date().getFullYear()}`,
		year_min: `${new Date().getFullYear() - 5}`,
	});
	const navigate = useNavigate();
	const [debouncedFilters] = useDebounce(filters, 500);

	useEffect(() => {
		async function getDetails() {
			const result = await getDefaultFilterValues();
			const defaultValues = {};
			const values = result.DEFAULT_CAR_VALUES;
			if (values.RADIUS) {
				defaultValues.radius = values.RADIUS;
			}
			if (values.MILEAGE) {
				defaultValues.mileage = values.MILEAGE;
			}
			if (values.MAX_PRICE) {
				defaultValues.price_max = values.MAX_PRICE;
			}
			if (values.MIN_PRICE) {
				defaultValues.price_min = values.MIN_PRICE;
			}
			if (values.MAX_YEAR) {
				defaultValues.year_max = values.MAX_YEAR;
			}
			if (values.MIN_YEAR) {
				defaultValues.year_min = values.MIN_YEAR;
			}
			if (Object.values(defaultValues).length) {
				setFilters((prev) => ({ ...prev, ...defaultValues }));
			}
		}
		getDetails();
	}, []);

	const clearCompare = () => {
		localStorage.setItem("compareCars", JSON.stringify([]));
		localStorage.setItem("compareSalvage", JSON.stringify([]));
		setCompare([]);
	};
	const handleSelection = () => {
		if (pageType === "MarketCars") {
			if (isUpdate) {
				navigate("/compareCars?update=true", {
					state: {
						CompareCars: compare,
						filters: filters,
						inputFormValues: formValues,
					},
				});
			} else {
				navigate("/compareCars", {
					state: {
						CompareCars: compare,
						filters: filters,
						inputFormValues: formValues,
					},
				});
			}
		} else {
			if (isUpdate) {
				navigate("/compareSalvage?update=true", {
					state: {
						CompareCars: compare,
						filters: filters,
						inputFormValues: formValues,
					},
				});
			} else {
				navigate("/compareSalvage", {
					state: {
						CompareCars: compare,
						filters: filters,
						inputFormValues: formValues,
					},
				});
			}
		}
	};

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		if (debouncedFilters.year_min > debouncedFilters.year_max) {
			setCars([]);
			return;
		}
		if (pageType === "MarketCars") {
			setLoading(true);
			getCarListing(debouncedFilters, currentPage)
				.then((res) => {
					setLoading(false);
					if (res?.data) {
						setCars(res?.data?.records);
						setTotalCount(res?.data?.totalCount);
						setNoOfPages(parseInt(res?.data?.totalCount / 20) + 1);
					}
				})
				.catch(() => {
					setLoading(false);
				});
		}
		if (pageType === "SalvageCars") {
			setLoading(true);
			getSalvageCarListing(debouncedFilters, currentPage)
				.then((res) => {
					setLoading(false);
					if (res?.data) {
						setCars(res?.data);
						setTotalCount(res?.totalCount);
						setNoOfPages(parseInt(res?.totalCount / 20) + 1);
					}
				})
				.catch(() => {
					setLoading(false);
				});
		}
	}, [debouncedFilters, currentPage, pageType]);

	useEffect(() => {
		setCurrentPage(1);
	}, [
		debouncedFilters.make,
		debouncedFilters.year_min,
		debouncedFilters.year_max,
		debouncedFilters.price_min,
		debouncedFilters.mileage,
		debouncedFilters.radius,
		debouncedFilters.price_max,
		debouncedFilters.model,
		debouncedFilters.city,
		debouncedFilters.trim,
	]);

	useEffect(() => {
		if (location?.state?.filterInput) {
			const {
				state: {
					filterInput: { make, model, trim, city, year, year_min, year_max },
				},
			} = location;
			setFilters({
				...filters,
				make: make ? make : "",
				model: model ? model : "",
				trim: trim ? trim : "",
				city: city ? city : "",
				year_max: year ? year : year_max,
				year_min: year ? year : year_min,
			});
			localStorage.setItem(
				"filters",
				JSON.stringify({
					...filters,
					make: make ? make : "",
					model: model ? model : "",
					trim: trim ? trim : "",
					city: city ? city : "",
					radius: filters?.radius ?? 50,
					year_max: year ? year : year_max,
					year_min: year ? year : year_min,
				}),
			);
		}
		if (location?.state?.formInputValues) {
			setFormValues(location?.state?.formInputValues);
			localStorage.setItem(
				"formValues",
				JSON.stringify(location?.state?.formInputValues),
			);
		}
		if (pageType === "MarketCars") {
			const res = JSON.parse(localStorage.getItem("compareCars"));
			if (res?.length) {
				setCompare(res);
			}
		}
		if (pageType === "SalvageCars") {
			const filters = JSON.parse(localStorage.getItem("filters"));
			setFilters(filters);
			const inputValues = JSON.parse(localStorage.getItem("formValues"));
			setFormValues(inputValues);
			const res = JSON.parse(localStorage.getItem("compareSalvage"));
			if (res?.length) {
				setCompare(res);
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location, pageType]);

	return (
		<div className="find-car-page">
			<Spin
				spinning={loading}
				indicator={
					<LoadingOutlined
						style={{
							fontSize: 48,
							color: "#38bdf8",
						}}
						spin
					/>
				}
			>
				{/* Hero Section */}
				<div className="find-car-hero">
					<h1 className="text-center text-3xl md:text-4xl font-bold text-white">
						{heading}
					</h1>
					<p className="text-center text-gray-400 mt-2">
						Find and compare vehicles from our extensive database
					</p>
				</div>

				{compare.length > 0 && (
					<div className="mt-4 flex justify-center px-4">
						<div className="border border-cyan-500/30 w-full md:w-[55%] bg-slate-800/80 backdrop-blur-sm px-4 py-3 flex justify-between rounded-xl">
							<div className="text-gray-300">
								{compare.length === 4 ? (
									<p>
										You've reached the 4 vehicle max. Remove a vehicle to add
										another.
									</p>
								) : (
									<p>
										You've added{" "}
										<span className="text-cyan-400 font-semibold">
											{compare.length}
										</span>{" "}
										of 4 vehicles.
									</p>
								)}
								{
									<button
										type="button"
										className="text-cyan-400 hover:text-cyan-300 underline cursor-pointer transition-colors"
										onClick={handleSelection}
									>
										{" "}
										See your Selections
									</button>
								}
							</div>
							<div className="flex items-center">
								<CloseOutlined
									className="cursor-pointer text-gray-400 hover:text-white transition-colors"
									onClick={() => clearCompare()}
								/>
							</div>
						</div>
					</div>
				)}

				<div
					className={`${compare.length < 1 ? "pt-6" : "pt-4"} pb-16 mx-auto flex gap-6 max-w-[1120px] flex-col md:flex-row px-4`}
				>
					<div className="md:w-[30%] w-full flex gap-3 flex-col">
						<div>
							<Collapse
								size="large"
								items={[
									{
										key: "1",
										label: "Damaged Vehicle Detail",
										children: <DamagedVehicle formValues={formValues} />,
									},
								]}
								className="findCarCollapse dark-collapse"
								expandIconPosition="end"
							/>
						</div>
						<div>
							<Collapse
								size="large"
								defaultActiveKey="1"
								items={[
									{
										key: "1",
										label: "Apply Filters",
										children: (
											<Filter
												filters={filters}
												setFilters={setFilters}
												debouncedFilters={debouncedFilters}
											/>
										),
									},
								]}
								className="findCarCollapse dark-collapse"
								expandIconPosition="end"
							/>
						</div>
					</div>

					{cars.length > 0 ? (
						<div className="md:w-[70%] w-full">
							{/* Results Header */}
							<div className="find-car-results-header">
								<div className="find-car-results-count">
									<span>{totalCount.toLocaleString()}</span> vehicles found
								</div>
							</div>

							{/* Vehicle Grid */}
							<div className="vehicle-grid max-h-[1215px] overflow-x-hidden overflow-y-auto pr-2">
								{cars?.map((carDetail) => {
									return pageType === "SalvageCars" ? (
										<SalvageCarInfo
											carDetail={carDetail}
											key={carDetail?.id}
											compare={compare}
											setCompare={setCompare}
											setLoading={setLoading}
											pageType={pageType}
										/>
									) : (
										<MarketCarInfo
											carDetail={carDetail}
											key={carDetail?.id}
											compare={compare}
											setCompare={setCompare}
											setLoading={setLoading}
											pageType={pageType}
										/>
									);
								})}
							</div>

							{/* Pagination */}
							<div className="pagination-container">
								<button
									type="button"
									className="pagination-btn"
									onClick={() =>
										setCurrentPage((prev) => Math.max(1, prev - 1))
									}
									disabled={currentPage === 1}
								>
									<svg
										className="w-4 h-4"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										aria-hidden="true"
									>
										<path
											d="M19 12H5M12 19l-7-7 7-7"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
									Previous
								</button>
								<span className="text-slate-500 text-sm">
									Page {currentPage} of {noOfPages}
								</span>
								<button
									type="button"
									className="pagination-btn pagination-btn-next"
									onClick={() => setCurrentPage((prev) => prev + 1)}
									disabled={currentPage >= noOfPages}
								>
									Next
									<svg
										className="w-4 h-4"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										aria-hidden="true"
									>
										<path
											d="M5 12h14M12 5l7 7-7 7"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</button>
							</div>
						</div>
					) : (
						<div className="md:w-[70%] w-full">
							<div className="empty-state">
								<svg
									className="empty-state-icon"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="1"
									aria-hidden="true"
								>
									<rect x="2" y="6" width="20" height="12" rx="2" />
									<circle cx="7" cy="18" r="2" />
									<circle cx="17" cy="18" r="2" />
									<path d="M5 6l2-3h10l2 3" />
									<path d="M9 10h6M9 14h4" strokeLinecap="round" />
								</svg>
								<h3 className="empty-state-title">No Vehicles Found</h3>
								<p className="empty-state-text">
									Try adjusting your filters to find more results
								</p>
							</div>
						</div>
					)}
				</div>
			</Spin>
		</div>
	);
};

export default FilteredCar;

export const Filter = ({ filters, setFilters, debouncedFilters }) => {
	const [modelOptions, setModelOptions] = useState([]);
	const [alertMessage, setAlertMessage] = useState("");
	const popoverContent = (
		<div style={{ maxWidth: "260px" }}>
			Please note that when filtering the results by Trim, the text must be an
			exact match, including uppercase, lowercase, spaces and symbols.
		</div>
	);
	useEffect(() => {
		const res =
			carModels[
				debouncedFilters.make
					.split(" ")
					.map((item) => item.charAt(0).toUpperCase() + item.slice(1))
					.join(" ")
			];
		if (res) {
			const filteredModels = res.map((item) => {
				return {
					label: item,
					value: item,
				};
			});
			setModelOptions(filteredModels);
		} else {
			setModelOptions([]);
		}
	}, [debouncedFilters.make]);
	const handleChange = (value, name) => {
		if (name === "year_max") {
			if (+filters.year_min > +value) {
				setAlertMessage("year max must be greater than year min");
			} else {
				setAlertMessage("");
			}
		}
		if (name === "year_min") {
			if (+filters.year_max < +value) {
				setAlertMessage("year max must be greater than year min");
			} else {
				setAlertMessage("");
			}
		}
		setFilters((prev) => ({ ...prev, [name]: value }));
	};

	return (
		<>
			<div className="border-b p-5">
				<div>
					<p className="font-light text-xs pt-4 pb-2">Make</p>
					<Input
						onChange={(e) => handleChange(e.target.value, "make")}
						value={filters.make}
						disabled
					/>
					<div>
						<p className="font-light text-xs pt-4 pb-2">Model</p>
						<AutoComplete
							style={{
								width: "100%",
							}}
							onChange={(e) => handleChange(e, "model")}
							value={filters.model}
							options={modelOptions}
							disabled
						/>
					</div>
					<div>
						<p className="font-light text-xs pt-4 pb-2">Zip Code</p>
						<Input
							onChange={(e) => handleChange(e.target.value, "city")}
							value={filters.city}
						/>
					</div>

					{/* <div>
            <p className='font-light text-xs pt-4 pb-2'>Price</p>
            <Input
              onChange={(e) => handleChange(e.target.value, 'price_max')}
              value={filters?.price_max}
            />
          </div> */}
					<div>
						<p className="font-light text-xs pt-4 pb-2">Trim</p>
						<Popover content={popoverContent} trigger="hover">
							<Input
								onChange={(e) => handleChange(e.target.value, "trim")}
								value={filters.trim}
							/>
						</Popover>
					</div>
					<div>
						<p className="font-light text-xs pt-4 pb-2">Radius</p>
						<Slider
							min={0}
							max={5000}
							defaultValue={50}
							onChange={(e) => handleChange(e, "radius")}
							value={filters.radius}
						/>
					</div>
				</div>
			</div>

			<div className="border-b p-5">
				<h2 className="font-semibold text-lg pb-4">Price</h2>
				<div className="flex items-end">
					<div>
						<p className="font-light text-xs pb-2">Min</p>
						<Input
							onChange={(e) => handleChange(e.target.value, "price_min")}
							type="number"
							value={filters?.price_min}
						/>
					</div>
					<p className="px-4 font-light text-lg">to</p>
					<div>
						<p className="font-light text-xs pb-2">Max</p>
						<Input
							onChange={(e) => handleChange(e.target.value, "price_max")}
							type="number"
							value={filters?.price_max}
						/>
					</div>
				</div>
			</div>

			<div className="border-b p-5">
				<h2 className="font-semibold text-lg pb-4">Year</h2>
				<div className="flex items-end">
					<div>
						<p className="font-light text-xs pb-2">Min</p>
						<Input
							onChange={(e) => handleChange(e.target.value, "year_min")}
							value={filters?.year_min}
						/>
					</div>
					<p className="px-4 font-light text-lg">to</p>
					<div>
						<p className="font-light text-xs pb-2">Max</p>
						<Input
							onChange={(e) => handleChange(e.target.value, "year_max")}
							value={filters?.year_max}
							type="number"
						/>
					</div>
				</div>
				<p className="text-red-500 text-xs mt-4">{alertMessage}</p>
			</div>
			<div className="border-b p-5">
				<h2 className="font-semibold text-lg pb-4">Mileage</h2>
				<div className="flex items-end">
					<Input
						onChange={(e) => handleChange(e.target.value, "mileage")}
						value={filters.mileage}
					/>
				</div>
			</div>
		</>
	);
};

// export const SearchVin = ({ handleVinSearch }) => {
//   const [vin, setVin] = useState("");

//   return (
//     <>
//       <div className='border-b p-5'>
//         <div>
//           <p className='font-light text-xs pt-4 pb-2'>VIN Number</p>
//           <Input
//             onChange={(e) => setVin(e.target.value)}
//             value={vin}
//           />
//           <button
//             className='bg-[#e5e7eb] text-black py-3 mb-2 w-full rounded-xl font-medium mt-6'
//             onClick={() => handleVinSearch(vin, setVin)}
//           >
//             Search
//           </button>
//         </div>
//       </div>
//     </>
//   );
// };

export const DamagedVehicle = ({ formValues }) => {
	const { vin, mileage, modelYear, make, model } = formValues;
	console.log("form", formValues);
	return (
		<>
			<div className="border-b p-5">
				<div className="mt-2">
					<p className="font-light text-xs pb-2">
						<b>
							{modelYear} {make} {model}
						</b>
					</p>
					<p className="font-light text-xs pb-2">
						<b>VIN:</b> {vin}
					</p>
					<p className="font-light text-xs pb-2">
						<b>Mileage:</b> {mileage}
					</p>
				</div>
			</div>
		</>
	);
};
